import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, Renderer2, effect, inject, input, signal } from '@angular/core';
import { IProductDataV3, IProductMediaItem, Product } from '@df/catalog/product/product';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { BodyClassService } from '@df/ui/body-class.service';
import { DfUiModule } from '@df/ui/df-ui.module';
import { PhotoSwipeModule } from '@df/ui/photoswipe/photoswipe.module';
import { PhotoswipeOptions } from '@df/ui/photoswipe/photoswipe.types';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import Swiper from 'swiper';
import { ProductWishlistToggleComponent } from '../../wishlist-toggle/product-wishlist-toggle.component';
import { BadgeComponent } from './../../../../common/badge/badge.component';
import { ImageVideoComponent } from './../../../../common/image-video/image-video.component';

@Component({
    selector: 'product-view-media',
    templateUrl: 'product-view-media.html',
    styleUrls: ['product-view-media.scss', 'photoswipe.scss', '../../../../common/image-video/has-video-zoom.scss'],
    imports: [
        DfUiModule,
        GridModule,
        DfModuleImageModule,
        ImageVideoComponent,
        PhotoSwipeModule,
        TranslationModule,
        DfUiCarouselModule,
        BadgeComponent,
        ProductWishlistToggleComponent,
        NgTemplateOutlet
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewMediaComponent extends UiComponent implements OnChanges {
    /**
     * Source product
     */
    source = input.required<Product<IProductDataV3>>();

    protected media?: Array<IProductMediaItem & { _trackId: string }>;
    protected swiper?: Swiper;
    protected photoswipeOptions: Partial<PhotoswipeOptions> = {};
    protected readonly activeIndex = signal<number>(0);

    private elementRef = inject(ElementRef);
    private renderer2 = inject(Renderer2);
    private bodyClassService = inject(BodyClassService);

    private sourceEffect = effect(() => {
        const source = this.source();
        this.media = undefined;

        if (source.data?.media) {
            const sourceId = source.id;
            const media = [...source.data?.media].map((item, index) => ({
                ...item,
                _trackId: `${sourceId}-${index}`
            }));

            const baseMediaIndex = media.findIndex(i => i.labels?.includes('base'));
            if (baseMediaIndex > -1) {
                media.unshift(media.splice(baseMediaIndex, 1)[0]);
            }
            this.media = media;
            this.activeIndex.set(0);
        }
        this.detectChanges();
    });

    override ngOnInit(): void {
        const element = this.elementRef.nativeElement;

        this.photoswipeOptions = {
            showAnimationDuration: 250,
            hideAnimationDuration: 250,
            pinchToClose: false,
            closeOnScroll: false,
            closeOnVerticalDrag: false,
            tapToToggleControls: false,
            hiddenControlsMobile: false,
            counterEl: false,
            barsSize: { top: 0, bottom: 'auto' },
            initialZoom: 1,
            followMouse: true,
            canPreload: false,
            timeToIdle: 0,
            loadAfterOpen: true,
            imageOptions: {
                width: 2500,
                height: 2500
            },
            getDoubleTapZoom: (isMouseClick, item: any) => {
                if (!item.zoomLevel) {
                    item.zoomLevel = item.initialZoomLevel;
                }

                let res: number;

                if (item.zoomLevel < 0.5 && (this.client.isM || this.client.isS)) {
                    res = 0.5;

                    this.renderer2.addClass(element, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(element, 'photoswipe--zoom-100');
                } else if (item.zoomLevel < 1) {
                    res = 1;

                    this.renderer2.addClass(element, 'photoswipe--zoom-100');
                    this.renderer2.removeClass(element, 'photoswipe--zoom-50');
                } else {
                    res = item.initialZoomLevel;

                    this.renderer2.removeClass(element, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(element, 'photoswipe--zoom-100');
                }

                item.zoomLevel = res;

                return res;
            }
        };

        super.ngOnInit();
    }

    getPhotoswipeOptions(index: number): Partial<PhotoswipeOptions> {
        const onFocusOnly = !!this.media?.[index]?.video;
        return {
            index,
            originalZoom: 6,
            galleryUID: this.source()?.id,
            onFocusOnly: onFocusOnly
        };
    }

    onAfterInit(swiper: Swiper) {
        if (Array.isArray(swiper)) {
            swiper = (swiper as any as Swiper[])[0];
        }
        this.swiper = swiper;
        this.swiper?.slideTo(0, 0);

        this.detectChanges();
    }

    onPhotoswipeStateChange(state: boolean) {
        this.bodyClassService.set('photoswipe-open', this, state);

        state
            ? this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--active')
            : this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--active');
        this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--inactive');

        this.timeoutService.setTimeout(() => {
            this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--inactive');
        }, 500);
    }

    onPhotoswipeChanged(index: number) {
        this.activeIndex.set(index);
        if (this.client.sizeId < 2) {
            this.swiper?.slideTo(index);
        }
    }
}
